import React from "react";
import PropTypes from "prop-types";
import { prProfileUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../../profiles/SectionCard";
import SectionSkeleton from "../../../profiles/SectionSkeleton";
import ListRow from "../../../profiles/ListRow";

/**
 * PeopleCard — chrome half of the company page's "People (N Users)" rail card (the data half is
 * useCompanyPeople). One row per distinct current team member, same divider-list shape as the
 * "Our PR Representatives"/"Our PR Agencies" rail cards. `N` in the title is the length of `members` — the
 * hook has already deduped to distinct people and paged through every matching job row, so this
 * count is never a page-bound undercount.
 *
 * Purely presentational and self-collapsing: a skeleton while the roster loads, nothing at all once
 * it settles empty. Whether an empty card collapses the whole rail is the page's call.
 *
 * @param {Object} props
 * @param {Array} props.members - `{ id, name, imageUrl, slug, title }` rows, already deduped
 * @param {boolean} [props.isLoading] - True while the roster read is in flight
 */
function PeopleCard({ members, isLoading = false }) {
  if (isLoading) return <SectionSkeleton lines={3} />;
  if (!members.length) return null;

  const label = members.length === 1 ? "1 User" : `${members.length} Users`;

  return (
    <SectionCard id="company-rail-people" title={`People (${label})`}>
      <ul className="list-unstyled mb-0">
        {members.map((person) => (
          <li className="ms-1 mb-2 animated faster fadeIn" key={person.id}>
            <ListRow
              href={person.slug ? prProfileUrl({ slug: person.slug, migrate_id: person.migrateId }) : null}
              imageUrl={person.imageUrl}
              title={person.name}
              subtitleTitle={person.title}
            />
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

PeopleCard.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      imageUrl: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
};

export default PeopleCard;
